import type { BaseDocument } from '../mongo/base-document.interface';
import type { GeoCoordinates, Location, PostalAddress } from '../geolocation';
import type { PhoneNumber } from '../phone';
import type { SyncResource } from '../sync/sync-resource';

export const SITE_TYPE = {
  RELAY: 'RELAY',
  SHOP: 'SHOP',
  WAREHOUSE: 'WAREHOUSE',
} as const;

export type SiteType = (typeof SITE_TYPE)[keyof typeof SITE_TYPE];

/**
 * Full Site entity as stored in MongoDB.
 * A Site belongs to a Space within a Tenant.
 */
export interface Site extends BaseDocument {
  code: string;
  color: string;
  customerSupportPhone?: PhoneNumber;
  deliveryTimeslotDurationInHours?: number;
  description?: string;
  location?: Location;
  name: string;
  phone?: PhoneNumber;
  spaceId: string;
  type: SiteType;
}

/**
 * Site enriched with the display name of its parent space, used for cross-space search
 * results where the parent space document may not be independently readable by the caller.
 */
export interface SiteWithSpaceName extends Site {
  spaceName: string;
}

/**
 * Minimal Site reference embedded (denormalized) into other documents.
 * `address` and `coordinates` are flat snapshots of `site.location.address` and
 * `site.location.coordinates` at embed time. `phone` is a flat snapshot of `site.phone`.
 * All three optional so existing stored documents without them remain valid.
 */
export type SiteSummary = Pick<Site, 'code' | 'color' | 'id' | 'name' | 'type'> & {
  address?: PostalAddress;
  coordinates?: GeoCoordinates;
  phone?: PhoneNumber;
};

/**
 * Payload for creating a new Site.
 */
export type CreateSite = Omit<Site, 'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'>;

/**
 * Payload for updating an existing Site via PATCH.
 */
export interface UpdateSite {
  code?: string;
  color?: string;
  customerSupportPhone?: PhoneNumber | null;
  deliveryTimeslotDurationInHours?: number;
  location?: Location;
  name?: string;
  phone?: PhoneNumber | null;
  type?: SiteType;
}

// ─── Sync resource descriptor (declaration-merged with the Site interface) ────

/**
 * Runtime descriptor for Site collection synchronization.
 * Use `@SyncSource(Site)` on a service to publish create/update/delete events.
 */
export const Site: SyncResource<Site> = {
  collection: 'sites',
  name: 'site',
};
